import { useCallback, useEffect, useMemo, useState } from 'react';
import { GridCellProps } from 'react-virtualized';
import isNil from 'lodash/isNil';
import {
  CohortSampleOperationKey,
  ICohortSampleTable,
  Order,
  PermissionKey,
} from '../../../utils/apollo/graphqlSchema';
import { assertCondition } from '../../../utils/types';
import {
  exportTableToCsv,
  useTableColumnSortState,
  useTableColumnWidthState,
  useTableSearchState,
} from '../../MultiGridTable';
import * as CohortSampleTableUI from './CohortSampleTable/CohortSampleTableUI';
import * as CohortSampleChartUI from './CohortSampleChartUI';
import CohortSampleTable from './CohortSampleTable';
import { useAppDispatch } from '../../../redux/hooks';
import {
  displayTextWithEmptySymbol,
  formatTextWithNumberValue,
  renderCellBackground,
} from './utils';
import moment from 'moment';
import { SUBJECT_EVENT_KEY } from '../constants/utils';
import { setInspectSubjectIdAction } from '../../../redux/slices/analyses';
import usePermission from '../../../utils/hooks/permissionHooks';
import { globalFormat } from '../../../utils/formatting';
import { formatNumberWithComma } from '../../../utils/formatText';
import UnSampleButton from '../UnSampleButton';
import KubitTooltip from '../../KubitTooltip';
import { SecondaryButton } from '../../Button';
import CohortSampleChartSaveCohortAction from './CohortSampleChartSaveCohortAction';
import { ChartMode } from '../types';
import { TableSortIcon } from '../../Icon';
import { useChartContext } from '../ChartContext';
import { IS_SCREENSHOT_MODE } from '../../../config/constants';

interface IProps {
  analysisId: string;
  display: string;
  table: ICohortSampleTable;
  mode: ChartMode;
}

const DEFAULT_CELL_WIDTH = 160;
const COHORT_LAST_SEEN_DATE_FORMAT = 'M/D/YYYY HH:mm:ss:SSS';

const CohortSampleChartTable = ({
  analysisId,
  display,
  table,
  mode,
}: IProps) => {
  const dispatch = useAppDispatch();
  const {
    metadata: { query, isEmbedded },
  } = useChartContext();
  const { dateFormat, timeFormat } = globalFormat.getGlobalFormat();
  assertCondition(query.__typename === 'CohortSampleQuery');
  const { cohort, operation, schema } = query;
  const schemaId = schema.id;
  const isCompactMode = mode === ChartMode.Compact;

  const { checkIsPermitted } = usePermission();
  const isPermittedToInspectSubject = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.CohortSubjectInspect],
    schemaId,
  });

  const { sortColumnIndex, sortDirection, onSort } =
    useTableColumnSortState(analysisId);
  const { searchTerm, onSearch } = useTableSearchState(analysisId);
  const [isSearching, setIsSearching] = useState<boolean>(false);

  const handleSearch = (searchTerm: string) => {
    setIsSearching(searchTerm.length > 0);
    onSearch(searchTerm);
  };

  useEffect(() => {
    // Reset the isSearching state whenever the table data changes.
    // This ensures that the search bar is hidden when switching to a different table with no data,
    // even if a search was previously active.
    setIsSearching(false);
  }, [table]);

  const { headers, rows, columns } = useMemo(() => {
    const columns = table.columns;
    const headers = columns.map((column) => column.header);
    const numRows = columns[0].cells.length;
    const rows = Array.from({ length: numRows }, (_, rowIndex) =>
      columns.map((column) => column.cells[rowIndex]),
    );

    return {
      headers,
      rows,
      columns,
    };
  }, [table]);

  const sortedRows = useMemo(() => {
    let result = [...rows];

    if (searchTerm?.length) {
      result = result.filter((row) =>
        // Check if any cell in the row includes the searchTerm
        row.some((cell) => {
          const stringValue = cell.value.toString();
          return (
            stringValue.length &&
            stringValue.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }),
      );
    }

    // Sort the filtered data based on sortColumnIndex and sortDirection
    if (!isNil(sortColumnIndex)) {
      result.sort((a, b) => {
        const aValue = a[sortColumnIndex].value;
        const bValue = b[sortColumnIndex].value;

        return sortDirection === Order.Asc
          ? aValue.localeCompare(bValue, 'en', { numeric: true })
          : bValue.localeCompare(aValue, 'en', { numeric: true });
      });
    }

    // Return the filtered and sorted data for rendering
    return result;
  }, [sortColumnIndex, sortDirection, rows, searchTerm]);

  const headersCount = headers.length;
  const rowCount = sortedRows.length;
  const dataExists = rowCount > 0;

  const initialColumnWidths = Array.from({ length: headersCount }).map(
    () => DEFAULT_CELL_WIDTH,
  );

  const {
    columnWidths,
    getColumnWidth,
    onChangeColumnWidths,
    handleMouseDown,
    multiGridWidth,
    setMultiGridWidth,
  } = useTableColumnWidthState({
    analysisId,
    initialColumnWidths,
  });

  // XXX in case of compound cohort - we use the first simple cohort subject
  const cohortSubject =
    'subject' in cohort ? cohort.subject : cohort.simpleCohorts[0].subject;
  const isAllowSubjectInspect =
    // if operation is null, it means only on cohort row.
    // if not null, inspect is not available in Compare operation
    (isNil(operation) || operation.key !== CohortSampleOperationKey.Compare) &&
    // hard code as special case: not allowed to inspect when subject is EVENT
    cohortSubject.key !== SUBJECT_EVENT_KEY &&
    isPermittedToInspectSubject;

  const onClickUserId = useCallback(
    (subjectId: string) => {
      if (!isAllowSubjectInspect) {
        return;
      }
      dispatch(setInspectSubjectIdAction({ analysisId, subjectId }));
    },
    [dispatch, analysisId, isAllowSubjectInspect],
  );

  const cellRenderer = useCallback(
    ({ columnIndex, key, rowIndex, style }: GridCellProps) => {
      if (rowIndex === 0) {
        const { header, subheader, tooltip } = headers[columnIndex];
        return (
          <CohortSampleTableUI.HeaderCell
            key={key}
            style={style}
            $columnIndex={columnIndex}
          >
            <CohortSampleTableUI.TableHeaderRow
              onClick={() => onSort({ index: columnIndex })}
            >
              <KubitTooltip
                content={tooltip.length > 0 ? tooltip : header}
                noOpenDelay={true}
              >
                <CohortSampleChartUI.FlexColumn>
                  <CohortSampleChartUI.HeaderTextWrapper>
                    {header}
                  </CohortSampleChartUI.HeaderTextWrapper>
                  {sortColumnIndex === columnIndex && !isNil(sortDirection) && (
                    <TableSortIcon isSortAsc={sortDirection === Order.Asc} />
                  )}
                </CohortSampleChartUI.FlexColumn>
              </KubitTooltip>
              <CohortSampleTableUI.Subheader>
                {formatTextWithNumberValue(subheader)}
              </CohortSampleTableUI.Subheader>
            </CohortSampleTableUI.TableHeaderRow>
            {rowIndex === 0 && (
              <CohortSampleTableUI.TableColumnResizeHandle
                onMouseDown={(e) => handleMouseDown(e, columnIndex)}
              />
            )}
          </CohortSampleTableUI.HeaderCell>
        );
      }

      const cellData = sortedRows[rowIndex - 1][columnIndex];

      if (cellData) {
        const columnType = columns[columnIndex]?.columnType;

        if (columnIndex === 0) {
          return (
            <CohortSampleTableUI.Cell
              key={key}
              style={style}
              title={cellData.value}
              $rowIndex={rowIndex}
              $columnIndex={columnIndex}
              $backgroundColor={renderCellBackground(
                columnType,
                cellData.value,
              )}
              onClick={() => onClickUserId(cellData.value)}
              $isClickable={isAllowSubjectInspect}
            >
              {displayTextWithEmptySymbol(cellData.value)}
            </CohortSampleTableUI.Cell>
          );
        }

        const isCellValueDate = moment(
          cellData.value,
          // special case, will change after kobol update schema for table to indicate which cell is date, and which
          // is number or string
          COHORT_LAST_SEEN_DATE_FORMAT,
          true,
        ).isValid();

        const getParsedDisplayValue = () => {
          if (isCellValueDate) {
            return globalFormat.formatDate(
              cellData.value,
              {
                dateFormat,
                timeFormat,
              },
              COHORT_LAST_SEEN_DATE_FORMAT,
            );
          } else {
            return formatNumberWithComma(cellData.value);
          }
        };
        const parsedValue = getParsedDisplayValue();
        return (
          <CohortSampleTableUI.Cell
            key={key}
            style={style}
            $rowIndex={rowIndex}
            $columnIndex={columnIndex}
            $backgroundColor={renderCellBackground(columnType, cellData.value)}
          >
            {cellData.value === 'inf' ? (
              <KubitTooltip
                placement="top-start"
                noOpenDelay={true}
                content="Infinite"
              >
                <CohortSampleTableUI.InfinityContainer>
                  &infin;
                </CohortSampleTableUI.InfinityContainer>
              </KubitTooltip>
            ) : (
              <KubitTooltip
                placement="top-start"
                noOpenDelay={true}
                content={parsedValue}
              >
                <span>{parsedValue}</span>
              </KubitTooltip>
            )}
          </CohortSampleTableUI.Cell>
        );
      }
    },
    [
      columns,
      dateFormat,
      handleMouseDown,
      headers,
      isAllowSubjectInspect,
      onClickUserId,
      onSort,
      sortColumnIndex,
      sortDirection,
      sortedRows,
      timeFormat,
    ],
  );

  const showReset = searchTerm.length > 0 || !isNil(sortColumnIndex);
  const onReset = () => {
    onSearch('');
    onSort({ index: null });
  };

  const onExportToCsv = () => {
    const data = sortedRows.map((row) => row.map((cell) => cell.value));
    exportTableToCsv({
      display: `${display}-${analysisId}`,
      headers: headers.map((h) => h.header),
      data,
    });
  };

  const showHeading =
    !IS_SCREENSHOT_MODE && !isCompactMode && (dataExists || isSearching);

  return (
    <>
      {showHeading && (
        <CohortSampleChartUI.FlexRow>
          <CohortSampleTableUI.SearchInputContainer>
            <CohortSampleTableUI.StyledSearchInput
              value={searchTerm}
              onChange={handleSearch}
            />
            {searchTerm.length > 0 && (
              <CohortSampleTableUI.SearchResultText>
                Found <strong>{rowCount}</strong> results.
              </CohortSampleTableUI.SearchResultText>
            )}
          </CohortSampleTableUI.SearchInputContainer>
          <CohortSampleTableUI.FlexRow>
            {!isNil(query.samplePercentage) &&
              query.samplePercentage !== 100 && (
                <UnSampleButton
                  analysisId={analysisId}
                  label={`Cohort is sampled at a rate of ${query.samplePercentage}%`}
                  disabled={query.schema.disabled}
                />
              )}
            {showReset && (
              <KubitTooltip content="Reset to default view" noOpenDelay={true}>
                <SecondaryButton
                  type="button"
                  onClick={onReset}
                  isOutlined
                  trackContext="cohorts"
                  trackAction="table - reset"
                >
                  Reset
                </SecondaryButton>
              </KubitTooltip>
            )}

            {!isEmbedded && (
              <CohortSampleChartSaveCohortAction
                analysisId={analysisId}
                schemaId={schemaId}
                cohortBuilderInfo={cohort}
                isCompareOperation={
                  operation?.key === CohortSampleOperationKey.Compare
                }
              />
            )}
            {dataExists && (
              <KubitTooltip
                content="Export current table to CSV"
                noOpenDelay={true}
              >
                <SecondaryButton
                  type="button"
                  onClick={onExportToCsv}
                  isOutlined
                  trackContext="cohorts"
                  trackAction="table - export csv"
                >
                  Export Table to CSV
                </SecondaryButton>
              </KubitTooltip>
            )}
          </CohortSampleTableUI.FlexRow>
        </CohortSampleChartUI.FlexRow>
      )}
      <CohortSampleTable
        cellRenderer={cellRenderer}
        headersCount={headersCount}
        columnCount={columns.length}
        rowCount={rowCount}
        columnWidths={columnWidths}
        getColumnWidth={getColumnWidth}
        multiGridWidth={multiGridWidth}
        onChangeColumnWidths={onChangeColumnWidths}
        setMultiGridWidth={setMultiGridWidth}
      />
    </>
  );
};

export default CohortSampleChartTable;
